import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Chip,
  InputAdornment,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormControl,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import type { Job, User } from '../../types';
import { apiRequest, api } from '../../utils/api';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatPhoneInput } from '../../utils/phoneUtils';
import AddressMapModal from '../../components/AddressMapModal';
import { useSocket } from '../../hooks/useSocket';

const statuses: Job['status'][] = [
  'Estimate',
  'Proposal',
  'Measure',
  'Shop Drawing',
  'Fabrication',
  'Install',
  'Finish',
];

interface Customer {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

// Helper functions for time conversion
const convertTo24Hour = (time12h: string): string => {
  if (!time12h || !time12h.includes(' ')) {
    // If already in 24-hour format or invalid, return as is
    if (time12h && time12h.includes(':')) {
      return time12h.split(' ')[0]; // Remove AM/PM if present
    }
    return '09:00';
  }
  const [time, modifier] = time12h.split(' ');
  let [hours, minutes] = time.split(':');
  if (hours === '12') {
    hours = '00';
  }
  if (modifier === 'PM') {
    hours = String(parseInt(hours, 10) + 12);
  }
  return `${hours.padStart(2, '0')}:${minutes || '00'}`;
};

const convertTo12Hour = (time24h: string): string => {
  if (!time24h) return '09:00 AM';
  const [hours, minutes] = time24h.split(':');
  const hour24 = parseInt(hours, 10);
  if (isNaN(hour24)) return '09:00 AM';
  const hour12 = hour24 % 12 || 12;
  const modifier = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes || '00'} ${modifier}`;
};


interface JobsProps {
  onJobClick?: (jobId: number) => void;
  jobs?: Job[];
  setJobs?: React.Dispatch<React.SetStateAction<Job[]>>;
}

export default function Jobs({ onJobClick, jobs: externalJobs, setJobs: setExternalJobs }: JobsProps = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [internalJobs, setInternalJobs] = useState<Job[]>([]);
  const jobs = externalJobs || internalJobs;
  const setJobs = setExternalJobs || setInternalJobs;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [statusFilter, setStatusFilter] = useState<Job['status'] | ''>('');
  const [payStatusFilter, setPayStatusFilter] = useState<Job['payStatus'] | ''>('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [jobNotifCounts, setJobNotifCounts] = useState<Record<number, number>>({});
  const { socket, isConnected } = useSocket();

  // Real-time bell count updates via Socket.IO
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (notification: { job_id: number | null }) => {
      if (notification.job_id) {
        setJobNotifCounts(prev => ({
          ...prev,
          [notification.job_id!]: (prev[notification.job_id!] || 0) + 1,
        }));
      }
    };

    socket.on('new-notification', handleNewNotification);
    return () => { socket.off('new-notification', handleNewNotification); };
  }, [socket, isConnected]);

  // Load per-job unread notification counts
  const loadJobNotifCounts = async () => {
    try {
      const response = await apiRequest('/notifications/unread-by-job', { method: 'GET' });
      setJobNotifCounts(response.data.counts || {});
    } catch (error) {
      console.error('Error loading job notification counts:', error);
    }
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([]);
  const [openCreateCustomerDialog, setOpenCreateCustomerDialog] = useState(false);
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [openJobAddressModal, setOpenJobAddressModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    status: 'active' as 'active' | 'inactive',
    createdAt: new Date().toISOString().split('T')[0],
  });
  const [newCustomerCompanyName, setNewCustomerCompanyName] = useState('');
  const [newCustomerContactPerson, setNewCustomerContactPerson] = useState('');
  const [formData, setFormData] = useState<Omit<Job, 'id'> & { customerId?: number | null }>({
    job: '',
    customer: '',
    customerAddress: '',
    jobAddress: '',
    apartment: '',
    status: 'Estimate',
    appointmentDate: new Date().toISOString().split('T')[0],
    startTime: '09:00 AM',
    endTime: '05:00 PM',
    dueDate: new Date().toISOString().split('T')[0],
    customerId: null,
    active: true,
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadCustomers = async () => {
    try {
      const response = await apiRequest('/customers', { method: 'GET' });
      const data = (response.data || []) as Customer[];
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const searchCustomers = async (searchTerm: string) => {
    try {
      if (!searchTerm.trim()) {
        // If no search term, load all customers
        const response = await apiRequest('/customers', { method: 'GET' });
        const data = (response.data || []) as Customer[];
        setCustomerSearchResults(data);
      } else {
        // Search with term
        const response = await apiRequest(`/customers?search=${encodeURIComponent(searchTerm)}`, { method: 'GET' });
        const data = (response.data || []) as Customer[];
        setCustomerSearchResults(data);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchResults([]);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        showActive: showActive.toString(),
        showInactive: showInactive.toString(),
      });
      
      if (statusFilter) {
        params.append('status', statusFilter);
      }
      if (payStatusFilter) {
        params.append('payStatus', payStatusFilter);
      }
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      const response = await apiRequest(`/jobs?${params.toString()}`, { method: 'GET' });
      const result = response.data;
      const data = result?.data || result || [];
      const mapped: Job[] = Array.isArray(data) ? data.map((j: any) => ({
        id: j.id,
        job: j.job_number,
        customer: j.customer_name || '',
        customerAddress: j.customer_address || '',
        jobAddress: j.job_address,
        apartment: j.apartment || '',
        status: (j.status || 'Estimate') as Job['status'],
        quoteStatus: j.quote_status as Job['quoteStatus'],
        payStatus: j.pay_status as Job['payStatus'],
        appointmentDate: j.appointment_date || '',
        startTime: j.start_time || '',
        endTime: j.end_time || '',
        dueDate: j.due_date || '',
        userId: j.user_id ?? undefined,
        active: j.active !== undefined ? j.active : true,
      })) : [];
      
      setJobs(mapped);
      
      // Update pagination info if available
      if (result?.pagination) {
        setTotalJobs(result.pagination.total);
      } else {
        // Fallback: if no pagination info, use array length
        setTotalJobs(mapped.length);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to load jobs',
      });
    } finally {
      setLoading(false);
    }
  };

  // Load current user profile to check role
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        setCurrentUser(response.data as User);
      } catch (error) {
        console.error('Failed to load current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if user is a Field Worker
  const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
  const isAdmin = currentUser?.role === 'SYSTEM ADMIN';

  useEffect(() => {
    loadJobs();
    loadJobNotifCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, showActive, showInactive, statusFilter, payStatusFilter, debouncedSearchTerm]);

  const handleRefresh = () => {
    loadJobs();
    setSnackbar({ open: true, message: 'Data refreshed' });
  };

  const handleCreate = () => {
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot create jobs' });
      return;
    }
    setEditingJob(null);
    setFormData({
      job: '',
      customer: '',
      customerAddress: '',
      jobAddress: '',
      apartment: '',
      status: 'Estimate',
      appointmentDate: new Date().toISOString().split('T')[0],
      startTime: '09:00 AM',
      endTime: '05:00 PM',
      dueDate: new Date().toISOString().split('T')[0],
      customerId: null,
      active: true,
    });
    setCustomerSearchInput('');
    setCustomerSearchResults([]);
    setOpenDialog(true);
    // Load all customers when dialog opens
    searchCustomers('');
  };

  const handleEdit = async (id: number) => {
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot edit jobs' });
      return;
    }
    const job = jobs.find((j) => j.id === id);
    if (job) {
      // Fetch full job details to get customer_id
      try {
        const response = await apiRequest(`/jobs/${id}`, { method: 'GET' });
        const jobData = response.data as any;
        setEditingJob(job);
        const customerId = jobData.customer_id || null;
        const selectedCustomer = customerId ? customers.find((c) => c.id === customerId) : null;
        
        setFormData({
          job: job.job,
          customer: job.customer,
          customerAddress: '',
          jobAddress: job.jobAddress,
          apartment: job.apartment,
          status: job.status,
          appointmentDate: job.appointmentDate,
          startTime: job.startTime,
          endTime: job.endTime,
          dueDate: job.dueDate,
          customerId,
          active: job.active !== undefined ? job.active : true,
        });
        
        // Set customer search input for autocomplete
        if (selectedCustomer) {
          setCustomerSearchInput(selectedCustomer.name);
          setCustomerSearchResults([selectedCustomer]);
        } else {
          setCustomerSearchInput(job.customer);
          setCustomerSearchResults([]);
        }
        
        setOpenDialog(true);
        // Load all customers when dialog opens
        searchCustomers('');
      } catch (error) {
        console.error('Error loading job details:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load job details',
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    if (!isAdmin) {
      setSnackbar({ open: true, message: 'Only SYSTEM ADMIN can delete jobs' });
      return;
    }
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (deleteId !== null) {
      try {
        await apiRequest(`/jobs/${deleteId}`, { method: 'DELETE' });
        // Remove from UI state after successful deletion
        setJobs(jobs.filter((j) => j.id !== deleteId));
        setSnackbar({ open: true, message: 'Job deleted successfully' });
        setOpenDeleteDialog(false);
        setDeleteId(null);
        // Reload jobs to update pagination count
        loadJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
        setSnackbar({
          open: true,
          message: error instanceof Error ? error.message : 'Failed to delete job',
        });
        setOpenDeleteDialog(false);
        setDeleteId(null);
      }
    }
  };

  const handleCreateCustomer = async () => {
    try {
      if (!newCustomerData.name.trim()) {
        setSnackbar({
          open: true,
          message: 'Customer name is required',
        });
        return;
      }

      if (!newCustomerData.email.trim()) {
        setSnackbar({
          open: true,
          message: 'Customer email is required',
        });
        return;
      }

      // Determine the actual name and contact person
      // If company name exists: name = company name, contact_person = contact person field (or name if not provided)
      // If no company name: name = person's name, contact_person = same as name (default)
      const isCompany = newCustomerCompanyName.trim() !== '';
      const finalName = isCompany ? newCustomerCompanyName.trim() : newCustomerData.name;
      const finalContactPerson = isCompany 
        ? (newCustomerContactPerson.trim() || newCustomerData.name || newCustomerCompanyName.trim()) 
        : (newCustomerContactPerson.trim() || newCustomerData.name);

      const customerData = {
        name: finalName,
        email: newCustomerData.email || null,
        phone: newCustomerData.phone || null,
        address: newCustomerData.address || null,
        company_name: isCompany ? newCustomerCompanyName.trim() : null,
        contact_person: finalContactPerson,
      };

      const response = await api.post('/customers', customerData);
      const newCustomer = response.data as Customer;
      
      // Add to customers list
      setCustomers([...customers, newCustomer]);
      
      // Auto-select the newly created customer
      const jobAddress = newCustomer.address || '';
      
      setFormData({
        ...formData,
        customerId: newCustomer.id,
        customer: newCustomer.name,
        jobAddress,
      });
      
      setCustomerSearchInput(newCustomer.name);
      setCustomerSearchResults([newCustomer]);
      
      // Reload all customers to ensure consistency
      await loadCustomers();
      
      // Close create customer dialog
      setOpenCreateCustomerDialog(false);
      setNewCustomerData({
        name: '',
        email: '',
        phone: '',
        address: '',
        status: 'active',
        createdAt: new Date().toISOString().split('T')[0],
      });
      setNewCustomerCompanyName('');
      setNewCustomerContactPerson('');
      
      setSnackbar({ open: true, message: 'Customer created and selected' });
    } catch (error) {
      console.error('Error creating customer:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (error as any)?.response?.data?.message || 'Failed to create customer';
      setSnackbar({
        open: true,
        message: errorMessage,
      });
    }
  };

  const handleSave = async () => {
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot create or edit jobs' });
      return;
    }
    try {
      const jobData = {
        job_number: formData.job || undefined,
        customer_id: formData.customerId || null,
        job_address: formData.jobAddress,
        apartment: formData.apartment || null,
        status: formData.status,
        appointment_date: formData.appointmentDate || null,
        start_time: formData.startTime || null,
        end_time: formData.endTime || null,
        due_date: formData.dueDate || null,
        active: formData.active !== undefined ? formData.active : true,
      };

      if (editingJob) {
        // Update existing job
        await api.put(`/jobs/${editingJob.id}`, jobData);
        setSnackbar({ open: true, message: 'Job updated successfully' });
      } else {
        // Create new job
        await api.post('/jobs', jobData);
        setSnackbar({ open: true, message: 'Job created successfully' });
      }
      loadJobs(); // Reload jobs from backend
      setOpenDialog(false);
      setEditingJob(null);
    } catch (error) {
      console.error('Error saving job:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to save job',
      });
    }
  };

    const getStatusColor = (status: Job['status']) => {
    switch (status) {
      case 'Estimate':
        return 'warning';
      case 'Proposal':
        return 'info';
      case 'Measure':
        return 'secondary';
      case 'Shop Drawing':
        return 'primary';
      case 'Fabrication':
        return 'primary';
      case 'Install':
        return 'success';
      case 'Finish':
        return 'success';
      default:
        return 'default';
    }
  };

  // Server-side filtering is now handled in loadJobs
  const displayedJobs = jobs;

  const handleStatusChange = async (jobId: number, newStatus: Job['status']) => {
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot change job status' });
      return;
    }
    try {
      await api.put(`/jobs/${jobId}`, { status: newStatus });
      // Update local state
      setJobs(jobs.map((job) => (job.id === jobId ? { ...job, status: newStatus } : job)));
      setSnackbar({ open: true, message: 'Status updated successfully' });
    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update status',
      });
    }
  };

  const handlePayStatusChange = async (jobId: number, newPayStatus: Job['payStatus']) => {
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot change payment status' });
      return;
    }
    try {
      await api.put(`/jobs/${jobId}`, { pay_status: newPayStatus });
      setJobs(jobs.map((job) => (job.id === jobId ? { ...job, payStatus: newPayStatus } : job)));
      setSnackbar({ open: true, message: 'Payment status updated successfully' });
    } catch (error) {
      console.error('Error updating payment status:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update payment status',
      });
    }
  };

  const handleActiveChange = async (jobId: number, active: boolean) => {
    if (isFieldWorker) {
      setSnackbar({ open: true, message: 'FIELD AND SHOP TEAM cannot change job active status' });
      return;
    }
    try {
      await api.put(`/jobs/${jobId}`, { active });
      // Reload jobs from backend to get updated data
      await loadJobs();
      setSnackbar({ open: true, message: `Job ${active ? 'activated' : 'deactivated'} successfully` });
    } catch (error) {
      console.error('Error updating active status:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to update active status',
      });
    }
  };

  const handleRowClick = (jobId: number, event: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons or select dropdowns
    const target = event.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('[role="button"]') ||
      target.closest('.MuiIconButton-root') ||
      target.closest('.MuiSelect-root') ||
      target.closest('.MuiInputBase-root') ||
      target.closest('.MuiCheckbox-root') ||
      target.closest('.MuiFormControlLabel-root')
    ) {
      return;
    }
    if (onJobClick) {
      onJobClick(jobId);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Jobs
        </Typography>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Jobs
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        {showSearch && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page when searching
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setShowSearch(false);
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              autoFocus
            />
          </Box>
        )}
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            justifyContent: 'space-between',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 1 : 0,
            py: isMobile ? 1 : 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <IconButton
              title="Search"
              onClick={() => setShowSearch(!showSearch)}
              color={showSearch ? 'primary' : 'default'}
            >
              <SearchIcon />
            </IconButton>
            <Box sx={{ display: 'flex', gap: isMobile ? 0 : 2, ml: isMobile ? 0 : 2, pl: isMobile ? 0 : 2, borderLeft: isMobile ? 0 : 1, borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showActive}
                    onChange={(e) => {
                      setShowActive(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInactive}
                    onChange={(e) => {
                      setShowInactive(e.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="Inactive"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: isMobile ? 'auto' : 0 }}>
            <IconButton title="Refresh" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
            {!isFieldWorker && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
              >
                Create
              </Button>
            )}
          </Box>
        </Toolbar>

        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150, flexShrink: 0 }}>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as Job['status'] | '');
                setPage(0);
              }}
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150, flexShrink: 0 }}>
            <TextField
              select
              label="Paid"
              value={payStatusFilter}
              onChange={(e) => {
                setPayStatusFilter(e.target.value as Job['payStatus'] | '');
                setPage(0);
              }}
              size="small"
            >
              <MenuItem value="">All Payment Statuses</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Unpaid">Unpaid</MenuItem>
            </TextField>
          </FormControl>
        </Box>

        {isMobile ? (
          /* Mobile card list */
          <Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : displayedJobs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No jobs found
              </Box>
            ) : (
              displayedJobs.map((job) => (
                <Box
                  key={job.id}
                  onClick={(e) => handleRowClick(job.id, e)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:active': { backgroundColor: 'action.selected' },
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  {/* Top row: job number + status chips */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {job.job || `Job #${job.id}`}
                      </Typography>
                      {jobNotifCounts[job.id] > 0 && (
                        <Badge
                          badgeContent={jobNotifCounts[job.id]}
                          color="error"
                          max={99}
                        >
                          <NotificationsIcon fontSize="small" color="action" />
                        </Badge>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {isFieldWorker ? (
                        <Chip
                          label={job.payStatus || 'Unpaid'}
                          color={job.payStatus === 'Paid' ? 'success' : 'default'}
                          size="small"
                        />
                      ) : (
                        <TextField
                          select
                          value={job.payStatus || 'Unpaid'}
                          onChange={(e) => {
                            e.stopPropagation();
                            handlePayStatusChange(job.id, e.target.value as Job['payStatus']);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ minWidth: 100, '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                        >
                          <MenuItem value="Paid">Paid</MenuItem>
                          <MenuItem value="Unpaid">Unpaid</MenuItem>
                        </TextField>
                      )}
                      <Chip
                        label={job.active !== undefined && job.active ? 'Active' : 'Inactive'}
                        color={job.active !== undefined && job.active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Customer */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {job.customer}
                  </Typography>

                  {/* Address */}
                  {job.jobAddress && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {job.jobAddress}
                    </Typography>
                  )}

                  {/* Apartment chips */}
                  {job.apartment && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                      {job.apartment.split(',').filter(a => a.trim()).map((apt, idx) => (
                        <Chip key={idx} label={apt.trim()} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}

                  {/* Status row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {isFieldWorker ? (
                      <Chip label={job.status} color={getStatusColor(job.status) as any} size="small" />
                    ) : (
                      <TextField
                        select
                        value={job.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(job.id, e.target.value as Job['status']);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                      >
                        {statuses.map((status) => (
                          <MenuItem key={status} value={status}>{status}</MenuItem>
                        ))}
                      </TextField>
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                      {!isFieldWorker && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(job.id); }} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {isAdmin && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(job.id); }} title="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Dates */}
                  {(job.appointmentDate || job.dueDate) && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      {job.appointmentDate && (
                        <Typography variant="caption" color="text.secondary">
                          Appt: {formatDateForDisplay(job.appointmentDate)}
                        </Typography>
                      )}
                      {job.dueDate && (
                        <Typography variant="caption" color="text.secondary">
                          Due: {formatDateForDisplay(job.dueDate)}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ))
            )}
            <TablePagination
              component="div"
              count={totalJobs}
              rowsPerPageOptions={[5, 10, 25]}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        ) : (
          /* Desktop table */
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Job</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Job Address</TableCell>
                    <TableCell>Apartment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Appointment Date</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : displayedJobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No jobs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedJobs.map((job) => (
                      <TableRow
                        key={job.id}
                        hover
                        onClick={(e) => handleRowClick(job.id, e)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {job.id}
                            {jobNotifCounts[job.id] > 0 && (
                              <Badge
                                badgeContent={jobNotifCounts[job.id]}
                                color="error"
                                max={99}
                              >
                                <NotificationsIcon fontSize="small" color="action" />
                              </Badge>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{job.job}</TableCell>
                        <TableCell>{job.customer}</TableCell>
                        <TableCell>{job.jobAddress}</TableCell>
                        <TableCell>
                          {job.apartment ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {job.apartment.split(',').filter(a => a.trim()).map((apt, idx) => (
                                <Chip key={idx} label={apt.trim()} size="small" />
                              ))}
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {isFieldWorker ? (
                            <Chip label={job.status} color={getStatusColor(job.status) as any} size="small" />
                          ) : (
                            <TextField
                              select
                              value={job.status}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(job.id, e.target.value as Job['status']);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              sx={{
                                minWidth: 140,
                                '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
                              }}
                              SelectProps={{
                                sx: { '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } },
                              }}
                            >
                              {statuses.map((status) => (
                                <MenuItem key={status} value={status}>{status}</MenuItem>
                              ))}
                            </TextField>
                          )}
                        </TableCell>
                        <TableCell>
                          {isFieldWorker ? (
                            <Chip
                              label={job.payStatus || 'Unpaid'}
                              color={job.payStatus === 'Paid' ? 'success' : 'default'}
                              size="small"
                            />
                          ) : (
                            <TextField
                              select
                              value={job.payStatus || 'Unpaid'}
                              onChange={(e) => {
                                e.stopPropagation();
                                handlePayStatusChange(job.id, e.target.value as Job['payStatus']);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              sx={{
                                minWidth: 100,
                                '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' },
                              }}
                              SelectProps={{
                                sx: { '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } },
                              }}
                            >
                              <MenuItem value="Paid">Paid</MenuItem>
                              <MenuItem value="Unpaid">Unpaid</MenuItem>
                            </TextField>
                          )}
                        </TableCell>
                        <TableCell>
                          {isFieldWorker ? (
                            <Chip
                              label={job.active !== undefined && job.active ? 'Active' : 'Inactive'}
                              color={job.active !== undefined && job.active ? 'success' : 'default'}
                              size="small"
                            />
                          ) : (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={job.active !== undefined ? job.active : true}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleActiveChange(job.id, e.target.checked);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              }
                              label={job.active !== undefined && job.active ? 'Active' : 'Inactive'}
                              labelPlacement="end"
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDateForDisplay(job.appointmentDate)} {job.startTime} - {job.endTime}</TableCell>
                        <TableCell>{formatDateForDisplay(job.dueDate)}</TableCell>
                        <TableCell align="right">
                          {!isFieldWorker && (
                            <IconButton size="small" onClick={() => handleEdit(job.id)} title="Edit">
                              <EditIcon />
                            </IconButton>
                          )}
                          {isAdmin && (
                            <IconButton size="small" onClick={() => handleDelete(job.id)} title="Delete">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalJobs}
              rowsPerPageOptions={[5, 10, 25, 50]}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingJob ? 'Edit Job' : 'Create New Job'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Autocomplete
              options={customerSearchResults}
              inputValue={customerSearchInput}
              onInputChange={(_event, newInputValue) => {
                setCustomerSearchInput(newInputValue);
                // Search customers (empty string will load all)
                searchCustomers(newInputValue);
              }}
              value={customerSearchResults.find((c) => c.id === formData.customerId) || customers.find((c) => c.id === formData.customerId) || null}
              onChange={(_event, newValue) => {
                if (newValue) {
                  const selectedCustomer = newValue as Customer;
                  
                  // Check if "Create New Customer" option was selected
                  if (selectedCustomer.id === -1) {
                    setNewCustomerData({
                      name: customerSearchInput,
                      email: '',
                      phone: '',
                      address: '',
                      status: 'active',
                      createdAt: new Date().toISOString().split('T')[0],
                    });
                    setNewCustomerCompanyName('');
                    setNewCustomerContactPerson('');
                    setOpenCreateCustomerDialog(true);
                    // Don't update formData yet, wait for customer creation
                    return;
                  }
                  
                  // Regular customer selection
                  const jobAddress = selectedCustomer.address || '';
                  setFormData({
                    ...formData,
                    customerId: selectedCustomer.id,
                    customer: selectedCustomer.name,
                    jobAddress,
                  });
                } else {
                  setFormData({
                    ...formData,
                    customerId: null,
                    customer: '',
                    jobAddress: '',
                  });
                }
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option.name}${option.phone ? ` - ${option.phone}` : ''}`;
              }}
              filterOptions={(options, params) => {
                // Show "Create New Customer" option if no results and user has typed something
                const filtered = [...options];
                const { inputValue } = params;
                
                if (inputValue && inputValue.trim() !== '') {
                  const isExisting = options.some(
                    (option) =>
                      option.name.toLowerCase() === inputValue.toLowerCase() ||
                      option.phone === inputValue
                  );
                  
                  if (!isExisting) {
                    filtered.push({
                      id: -1, // Special ID for "create new" option
                      name: inputValue,
                      email: '',
                      phone: '',
                      address: '',
                      city: '',
                      state: '',
                      zip_code: '',
                      country: '',
                    } as Customer);
                  }
                }
                
                return filtered;
              }}
              renderOption={(props, option) => {
                if (option.id === -1) {
                  return (
                    <li {...props} key="create-new">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                          + Create "{option.name}"
                        </Typography>
                      </Box>
                    </li>
                  );
                }
                return (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      {option.phone && (
                        <Typography variant="caption" color="text.secondary">
                          {option.phone}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Customer (Name or Phone)"
                  placeholder="Type to search..."
                  required
                />
              )}
              freeSolo={false}
              loading={false}
            />
            <TextField
              label="Job Address"
              value={formData.jobAddress}
              onClick={() => setOpenJobAddressModal(true)}
              onChange={(e) =>
                setFormData({ ...formData, jobAddress: e.target.value })
              }
              placeholder="Click to select address on map..."
              fullWidth
              required
              multiline
              rows={2}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenJobAddressModal(true)}
                      edge="end"
                      title="Open map to select address"
                    >
                      <MapIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : []}
              onChange={(_, newValue) => {
                const apartments = newValue.map(v => typeof v === 'string' ? v.trim() : v).filter(v => v);
                setFormData({ 
                  ...formData, 
                  apartment: apartments.join(',') 
                });
              }}
              onInputChange={(_, newInputValue, reason) => {
                // Handle comma-separated input when user types comma
                if (reason === 'input' && newInputValue.endsWith(',')) {
                  const valueBeforeComma = newInputValue.slice(0, -1).trim();
                  if (valueBeforeComma) {
                    const currentApartments = formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : [];
                    if (!currentApartments.includes(valueBeforeComma)) {
                      setFormData({ 
                        ...formData, 
                        apartment: [...currentApartments, valueBeforeComma].join(',') 
                      });
                    }
                    // Return empty to clear input - but this won't work directly
                    // We'll handle it via the input's onKeyDown
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Apartment"
                  placeholder="Type apartment and press Enter or comma to add"
                  onKeyDown={(e) => {
                    // Handle comma key
                    if (e.key === ',') {
                      e.preventDefault();
                      const input = e.currentTarget.querySelector('input') as HTMLInputElement;
                      if (input && input.value) {
                        const value = input.value.trim();
                        if (value) {
                          const currentApartments = formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : [];
                          if (!currentApartments.includes(value)) {
                            setFormData({ 
                              ...formData, 
                              apartment: [...currentApartments, value].join(',') 
                            });
                          }
                          // Clear the input
                          input.value = '';
                          // Trigger input change to update Autocomplete's internal state
                          const inputEvent = new Event('input', { bubbles: true });
                          input.dispatchEvent(inputEvent);
                        }
                      }
                    }
                  }}
                  fullWidth
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={index}
                    onDelete={() => {
                      const currentApartments = formData.apartment ? formData.apartment.split(',').map(a => a.trim()).filter(a => a) : [];
                      const newApartments = currentApartments.filter((_, i) => i !== index);
                      setFormData({ 
                        ...formData, 
                        apartment: newApartments.join(',') 
                      });
                    }}
                  />
                ))
              }
            />
            <TextField
              select
              label="Status"
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as Job['status'],
                })
              }
              fullWidth
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Appointment Date"
                type="date"
                value={formData.appointmentDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    appointmentDate: e.target.value,
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dueDate: e.target.value,
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Start Time"
                type="time"
                value={convertTo24Hour(formData.startTime)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    startTime: convertTo12Hour(e.target.value),
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="End Time"
                type="time"
                value={convertTo24Hour(formData.endTime)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    endTime: convertTo12Hour(e.target.value),
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.active !== undefined ? formData.active : true}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      active: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.customerId || !formData.jobAddress}
          >
            {editingJob ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Customer Dialog */}
      <Dialog
        open={openCreateCustomerDialog}
        onClose={() => setOpenCreateCustomerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Company Name (Optional)"
              value={newCustomerCompanyName}
              onChange={(e) => {
                setNewCustomerCompanyName(e.target.value);
                if (!e.target.value.trim()) {
                  setNewCustomerContactPerson('');
                }
              }}
              fullWidth
              placeholder="Leave empty if customer is an individual"
              helperText="Leave empty if customer is not a company"
              autoFocus
            />
            <TextField
              label="Contact Person"
              value={newCustomerContactPerson}
              onChange={(e) => setNewCustomerContactPerson(e.target.value)}
              fullWidth
              placeholder={newCustomerCompanyName.trim() ? "Enter contact person name" : newCustomerData.name || "Same as name"}
              helperText={newCustomerCompanyName.trim() ? "Contact person (different from company name)" : "Defaults to name if not specified"}
            />
            <TextField
              label={newCustomerCompanyName.trim() ? "Contact Person Name" : "Customer Name"}
              value={newCustomerData.name}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, name: e.target.value })
              }
              fullWidth
              required
              helperText={newCustomerCompanyName.trim() ? "Name of the contact person at the company" : "Customer name (person's name)"}
            />
            <TextField
              label="Email"
              type="text"
              value={newCustomerData.email}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, email: e.target.value })
              }
              fullWidth
              required
              placeholder="email1@example.com, email2@example.com"
              helperText="Multiple emails can be separated by comma"
            />
            <TextField
              label="Phone"
              value={newCustomerData.phone}
              onChange={(e) => {
                const formatted = formatPhoneInput(e.target.value);
                setNewCustomerData({ ...newCustomerData, phone: formatted });
              }}
              fullWidth
              placeholder="(555) 123-4567"
              inputProps={{
                maxLength: 14, // (555) 123-4567
              }}
            />
            <TextField
              label="Address"
              value={newCustomerData.address || ''}
              onClick={() => setOpenAddressModal(true)}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, address: e.target.value })
              }
              placeholder="Click to select address on map..."
              multiline
              rows={2}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenAddressModal(true)}
                      edge="end"
                      title="Open map to select address"
                    >
                      <MapIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={newCustomerData.status}
              onChange={(e) =>
                setNewCustomerData({
                  ...newCustomerData,
                  status: e.target.value as 'active' | 'inactive',
                })
              }
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              label="Created At"
              value={newCustomerData.createdAt}
              onChange={(e) =>
                setNewCustomerData({ ...newCustomerData, createdAt: e.target.value })
              }
              fullWidth
              placeholder="MM/DD/YYYY"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateCustomerDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!newCustomerData.name.trim() || !newCustomerData.email.trim()}
          >
            Create & Select
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Map Modal - Using Google Maps with American standard address format */}
      <AddressMapModal
        open={openAddressModal}
        onClose={() => setOpenAddressModal(false)}
        onConfirm={(address) => {
          setNewCustomerData({ ...newCustomerData, address });
          setOpenAddressModal(false);
        }}
        initialAddress={newCustomerData.address}
      />

      {/* Job Address Map Modal - Using Google Maps with American standard address format */}
      <AddressMapModal
        open={openJobAddressModal}
        onClose={() => setOpenJobAddressModal(false)}
        onConfirm={(address) => {
          setFormData({ ...formData, jobAddress: address });
          setOpenJobAddressModal(false);
        }}
        initialAddress={formData.jobAddress}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this job? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
